import { createLogger } from '@/lib/logger'

import {
  CreateImportJobRequest,
  ImportFilePreviewResponse,
  ImportFileUploadResponse,
  ImportJob,
  ImportPreviewRequest,
  ModuleTypeEnum,
} from '@scrambl/shared'

import { emptyResponseToNull } from './api-client'
import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

const logger = createLogger('lib/api/imports.api')

class ImportsApiService extends BaseApi {
  constructor() {
    super({ baseEndpoint: '/imports', responseNormalization: true })
  }

  async uploadImportFile(
    moduleType: ModuleTypeEnum,
    file: File,
  ): Promise<ImportFileUploadResponse> {
    logger.info('[uploadImportFile] Uploading file', {
      moduleType,
      fileName: file.name,
      fileSize: file.size,
    })
    const formData = new FormData()
    formData.append('file', file)

    try {
      const result = await this.makeRequest<ImportFileUploadResponse>(
        `/imports/upload?moduleType=${moduleType}`,
        {
          method: 'POST',
          body: formData,
          skipJsonParse: false,
        },
      )
      logger.info('[uploadImportFile] Upload complete', { result })
      return result
    } catch (error) {
      logger.error('[uploadImportFile] Error', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  async previewImportFile(
    moduleType: ModuleTypeEnum,
    request: ImportPreviewRequest,
  ): Promise<ImportFilePreviewResponse> {
    logger.info('[previewImportFile] Requesting server parse', {
      moduleType,
      fileDocumentId: request.fileDocumentId,
    })
    try {
      const result = await this.makeRequest<ImportFilePreviewResponse>(
        `/imports/preview?moduleType=${moduleType}`,
        {
          method: 'POST',
          body: request,
        },
      )
      logger.info('[previewImportFile] Preview received', {
        headerCount: result.headers?.length,
        totalRecords: result.totalRecords,
        diagnosticCount: result.parserDiagnostics?.length,
      })
      return result
    } catch (error) {
      logger.error('[previewImportFile] Error', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  async createImportJob(request: CreateImportJobRequest): Promise<ImportJob> {
    logger.info('[createImportJob] Creating job', {
      fileDocumentId: request.fileDocumentId,
      totalRecords: request.totalRecords,
      fieldMappingsCount: request.fieldMappings?.length,
      options: request.options,
    })
    try {
      const result = await this.makeRequest<ImportJob>(`/imports/jobs`, {
        method: 'POST',
        body: request,
      })
      logger.info('[createImportJob] Job created', {
        jobId: result.id,
        status: result.status,
      })
      return result
    } catch (error) {
      logger.error('[createImportJob] Error', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  async getImportJob(jobId: string): Promise<ImportJob> {
    logger.info('[getImportJob] Getting job', { jobId })
    try {
      const result = await this.makeRequest<ImportJob>(
        `/imports/jobs/${jobId}`,
        {
          method: 'GET',
        },
      )
      logger.info('[getImportJob] Job retrieved', {
        jobId: result.id,
        status: result.status,
        processedRecords: result.processedRecords,
        totalRecords: result.totalRecords,
        errorCount: result.errorCount,
        errorMessage: result.errorMessage,
      })
      return result
    } catch (error) {
      logger.error('[getImportJob] Error', {
        jobId,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  async getActiveImportJob(
    moduleType: ModuleTypeEnum,
  ): Promise<ImportJob | null> {
    logger.info('[getActiveImportJob] Getting active job', { moduleType })
    try {
      // "No active job" is an empty 200, which apiClient reads as
      // { success: true } — truthy, but with none of ImportJob's fields.
      // Rendering that crashes the panel on the first count it formats.
      const result = emptyResponseToNull(
        await this.makeRequest<ImportJob | null>(
          `/imports/jobs?moduleType=${moduleType}`,
          {
            method: 'GET',
          },
        ),
      )
      logger.info('[getActiveImportJob] Result', {
        hasJob: !!result,
        jobId: result?.id,
        status: result?.status,
      })
      return result
    } catch (error) {
      logger.error('[getActiveImportJob] Error', {
        moduleType,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  async listImportJobHistory(
    moduleType: ModuleTypeEnum,
    limit?: number,
    collectionId?: string,
  ): Promise<ImportJob[]> {
    logger.info('[listImportJobHistory] Fetching history', {
      moduleType,
      limit,
      collectionId,
    })
    try {
      const query = [
        limit ? `&limit=${limit}` : '',
        collectionId ? `&collectionId=${collectionId}` : '',
      ].join('')
      const result = await this.makeRequest<ImportJob[]>(
        `/imports/jobs/history?moduleType=${moduleType}${query}`,
        {
          method: 'GET',
        },
      )
      logger.info('[listImportJobHistory] History retrieved', {
        moduleType,
        jobCount: result.length,
      })
      return result
    } catch (error) {
      logger.error('[listImportJobHistory] Error', {
        moduleType,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  async cancelImportJob(
    jobId: string,
    moduleType: ModuleTypeEnum,
  ): Promise<ImportJob> {
    logger.info('[cancelImportJob] Canceling job', { jobId, moduleType })
    try {
      const result = await this.makeRequest<ImportJob>(
        `/imports/jobs/${jobId}/cancel?moduleType=${moduleType}`,
        {
          method: 'POST',
        },
      )
      logger.info('[cancelImportJob] Job canceled', {
        jobId: result.id,
        status: result.status,
      })
      return result
    } catch (error) {
      logger.error('[cancelImportJob] Error', {
        jobId,
        moduleType,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }
}

export const importsApi = createSingletonApi(ImportsApiService)
